import Decimal from 'decimal.js';
import {
  PERIODS_PER_YEAR,
  type AmortRow,
  type BusinessDayRule,
  type LoanTerms,
  type PaymentFrequency,
  type RateBasis,
} from '../schemas/loan.schemas.js';

// Pure loan-amortization math, shared by the API (posting payments, the daily
// job, the cash-flow forecast) and the web (previewing a loan before it is
// saved) so the two can never disagree. See docs/claude/loans.md.
//
// Rounding rule, applied everywhere: a period's interest is the beginning
// balance × the periodic rate, rounded half-up to the cent. The periodic rate
// itself is never rounded — the Carries note's Exhibit A only reproduces with
// the full-precision rate.

const D = Decimal.clone({ precision: 30, rounding: Decimal.ROUND_HALF_UP });
const ZERO = new D(0);
const ONE = new D(1);

function cents(v: Decimal): Decimal {
  return v.toDecimalPlaces(2, Decimal.ROUND_HALF_UP);
}

// ─── Dates (ISO strings, no timezone involved) ────────────────────────────────

function parts(iso: string): [number, number, number] {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  return [y, m, d];
}

function iso(y: number, m: number, d: number): string {
  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

function toUtc(isoDate: string): Date {
  const [y, m, d] = parts(isoDate);
  return new Date(Date.UTC(y, m - 1, d));
}

function fromUtc(date: Date): string {
  return iso(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate());
}

export function addDays(isoDate: string, n: number): string {
  const d = toUtc(isoDate);
  d.setUTCDate(d.getUTCDate() + n);
  return fromUtc(d);
}

/** Month stepping with the day clamped to 28 so every month has the date (§17.6 rule). */
export function addMonthsClamped(isoDate: string, n: number): string {
  const [y, m, d] = parts(isoDate);
  const total = y * 12 + (m - 1) + n;
  return iso(Math.floor(total / 12), (total % 12) + 1, Math.min(d, 28));
}

export function addPeriods(isoDate: string, frequency: PaymentFrequency, n: number): string {
  switch (frequency) {
    case 'weekly':
      return addDays(isoDate, 7 * n);
    case 'biweekly':
      return addDays(isoDate, 14 * n);
    case 'monthly':
      return addMonthsClamped(isoDate, n);
    case 'quarterly':
      return addMonthsClamped(isoDate, 3 * n);
  }
}

export function daysBetween(fromIso: string, toIso: string): number {
  return Math.round((toUtc(toIso).getTime() - toUtc(fromIso).getTime()) / 86_400_000);
}

/** 0 = Sunday … 6 = Saturday */
export function dayOfWeek(isoDate: string): number {
  return toUtc(isoDate).getUTCDay();
}

// ─── US federal (Federal Reserve) bank holidays ───────────────────────────────

function nthWeekdayOfMonth(year: number, month: number, weekday: number, n: number): string {
  const first = dayOfWeek(iso(year, month, 1));
  const day = 1 + ((weekday - first + 7) % 7) + (n - 1) * 7;
  return iso(year, month, day);
}

function lastWeekdayOfMonth(year: number, month: number, weekday: number): string {
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const last = dayOfWeek(iso(year, month, lastDay));
  return iso(year, month, lastDay - ((last - weekday + 7) % 7));
}

/** A fixed-date holiday falling on a Sunday is observed on Monday. Saturday holidays are not shifted — the Fed keeps banks open on the Friday. */
function observed(isoDate: string): string {
  return dayOfWeek(isoDate) === 0 ? addDays(isoDate, 1) : isoDate;
}

const holidayCache = new Map<number, Set<string>>();

export function usBankHolidays(year: number): Set<string> {
  const cached = holidayCache.get(year);
  if (cached) return cached;
  const set = new Set<string>([
    observed(iso(year, 1, 1)), // New Year's Day
    nthWeekdayOfMonth(year, 1, 1, 3), // Martin Luther King Jr. Day
    nthWeekdayOfMonth(year, 2, 1, 3), // Presidents Day
    lastWeekdayOfMonth(year, 5, 1), // Memorial Day
    observed(iso(year, 6, 19)), // Juneteenth
    observed(iso(year, 7, 4)), // Independence Day
    nthWeekdayOfMonth(year, 9, 1, 1), // Labor Day
    nthWeekdayOfMonth(year, 10, 1, 2), // Columbus Day
    observed(iso(year, 11, 11)), // Veterans Day
    nthWeekdayOfMonth(year, 11, 4, 4), // Thanksgiving
    observed(iso(year, 12, 25)), // Christmas
  ]);
  // New Year's Day of the following year observed on this year's Dec 31 never
  // happens under the Sunday-only rule, so nothing to add here.
  holidayCache.set(year, set);
  return set;
}

export function isBusinessDay(isoDate: string): boolean {
  const dow = dayOfWeek(isoDate);
  if (dow === 0 || dow === 6) return false;
  return !usBankHolidays(parts(isoDate)[0]).has(isoDate);
}

export function nextBusinessDay(isoDate: string): string {
  let d = isoDate;
  let guard = 0;
  while (!isBusinessDay(d) && guard++ < 10) d = addDays(d, 1);
  return d;
}

export function shiftForBusinessDay(isoDate: string, rule: BusinessDayRule): string {
  return rule === 'next_business_day' ? nextBusinessDay(isoDate) : isoDate;
}

// ─── Rates and payments ───────────────────────────────────────────────────────

/** Full-precision periodic rate as a fraction (0.000979002185… for 5.22% annual-effective weekly). */
export function periodicRate(annualRatePct: string | number, rateBasis: RateBasis, frequency: PaymentFrequency): Decimal {
  const annual = new D(annualRatePct).div(100);
  if (annual.lessThanOrEqualTo(0)) return ZERO;
  const p = PERIODS_PER_YEAR[frequency];
  if (rateBasis === 'annual_effective') return annual.plus(1).pow(ONE.div(p)).minus(1);
  return annual.div(p);
}

/** Level payment that fully repays `principal` at `rate` over `periods` (0% = principal ÷ periods). */
export function levelPayment(principal: string, rate: Decimal, periods: number): string {
  const P = new D(principal);
  if (rate.lessThanOrEqualTo(0)) return cents(P.div(periods)).toFixed(2);
  const denom = ONE.minus(rate.plus(1).pow(-periods));
  return cents(P.times(rate).div(denom)).toFixed(2);
}

/** Days the daily accrual rate is spread over for the given frequency. */
function daysPerPeriod(frequency: PaymentFrequency): Decimal {
  switch (frequency) {
    case 'weekly':
      return new D(7);
    case 'biweekly':
      return new D(14);
    case 'monthly':
      return new D(365).div(12);
    case 'quarterly':
      return new D(365).div(4);
  }
}

export interface ResolvedTerms extends LoanTerms {
  numberOfPayments: number;
  paymentAmount: string;
  rate: Decimal;
  maturityDate: string;
}

/** Fill in the defaults: numberOfPayments = amortizationPeriods, paymentAmount = level payment. */
export function resolveTerms(terms: LoanTerms): ResolvedTerms {
  const rate = periodicRate(terms.annualRatePct, terms.rateBasis, terms.paymentFrequency);
  const numberOfPayments = terms.numberOfPayments ?? terms.amortizationPeriods;
  const paymentAmount = terms.paymentAmount ?? levelPayment(terms.principal, rate, terms.amortizationPeriods);
  const resolved: ResolvedTerms = { ...terms, numberOfPayments, paymentAmount, rate, maturityDate: '' };
  resolved.maturityDate = dueDate(resolved, numberOfPayments);
  return resolved;
}

/** Scheduled date before any business-day shift. */
export function unshiftedDueDate(terms: LoanTerms, periodNo: number): string {
  return addPeriods(terms.firstPaymentDate, terms.paymentFrequency, periodNo - 1);
}

/** The date the installment is actually due (shifted when the terms say so). */
export function dueDate(terms: LoanTerms, periodNo: number): string {
  return shiftForBusinessDay(unshiftedDueDate(terms, periodNo), terms.businessDayRule);
}

/** Interest for period N accrues from here: the origination date for period 1, else the prior unshifted due date. */
export function periodStart(terms: LoanTerms, periodNo: number): string {
  return periodNo <= 1 ? terms.originationDate : unshiftedDueDate(terms, periodNo - 1);
}

/**
 * Remaining schedule from a balance. With no options this is the origination
 * projection (the note's Exhibit A); pass the live balance and periods paid to
 * see what is still owed. The balloon, when any, is the final row.
 */
export function projectSchedule(
  terms: LoanTerms,
  opts: { fromBalance?: string; fromPeriodNo?: number } = {},
): AmortRow[] {
  const t = resolveTerms(terms);
  const pmt = new D(t.paymentAmount);
  const fullyAmortizing = t.numberOfPayments === t.amortizationPeriods;
  let balance = new D(opts.fromBalance ?? t.principal);
  const rows: AmortRow[] = [];

  for (let i = (opts.fromPeriodNo ?? 0) + 1; i <= t.numberOfPayments && balance.greaterThan(0); i++) {
    const interest = cents(balance.times(t.rate));
    let principal = pmt.minus(interest);
    let payment = pmt;
    if ((fullyAmortizing && i === t.numberOfPayments) || principal.greaterThanOrEqualTo(balance)) {
      principal = balance;
      payment = principal.plus(interest);
    }
    balance = balance.minus(principal);
    rows.push({
      periodNo: i,
      dueDate: dueDate(t, i),
      payment: payment.toFixed(2),
      interest: interest.toFixed(2),
      principal: principal.toFixed(2),
      balance: balance.toFixed(2),
      kind: 'scheduled',
    });
  }

  if (balance.greaterThan(0)) {
    rows.push({
      periodNo: t.numberOfPayments,
      dueDate: t.maturityDate,
      payment: balance.toFixed(2),
      interest: '0.00',
      principal: balance.toFixed(2),
      balance: '0.00',
      kind: 'balloon',
    });
  }
  return rows;
}

/** Interest for a partial period: daily rate (periodic ÷ days in a period) × actual days, rounded to the cent. */
export function accruedInterest(terms: LoanTerms, balance: string, fromIso: string, toIso: string): string {
  const days = daysBetween(fromIso, toIso);
  if (days <= 0) return '0.00';
  const rate = periodicRate(terms.annualRatePct, terms.rateBasis, terms.paymentFrequency);
  return cents(new D(balance).times(rate).div(daysPerPeriod(terms.paymentFrequency)).times(days)).toFixed(2);
}

/**
 * Split an amount paid against a balance: the period's interest first (never
 * more than the amount), then principal (never more than is owed).
 */
export function splitPayment(
  currentBalance: string,
  rate: Decimal | string,
  amount: string,
): { interest: string; principal: string; payment: string } {
  const bal = new D(currentBalance);
  const r = new D(rate);
  let interest = cents(bal.times(r));
  const amt = new D(amount);
  if (interest.greaterThan(amt)) interest = amt;
  let principal = amt.minus(interest);
  if (principal.greaterThan(bal)) principal = bal;
  return { interest: interest.toFixed(2), principal: principal.toFixed(2), payment: principal.plus(interest).toFixed(2) };
}

export interface LoanSummary {
  paymentAmount: string;
  numberOfPayments: number;
  maturityDate: string;
  balloon: string;
  totalInterest: string;
  totalPaid: string;
  /** Periodic rate as a percentage with enough digits to show it is not rounded. */
  periodicRatePct: string;
}

/** Headline figures for a preview tile row, computed from the origination projection. */
export function summarizeTerms(terms: LoanTerms): LoanSummary {
  const t = resolveTerms(terms);
  const rows = projectSchedule(t);
  let totalInterest = ZERO;
  let totalPaid = ZERO;
  let balloon = ZERO;
  for (const r of rows) {
    if (r.kind === 'balloon') balloon = new D(r.payment);
    else {
      totalInterest = totalInterest.plus(r.interest);
      totalPaid = totalPaid.plus(r.payment);
    }
  }
  return {
    paymentAmount: t.paymentAmount,
    numberOfPayments: t.numberOfPayments,
    maturityDate: t.maturityDate,
    balloon: balloon.toFixed(2),
    totalInterest: totalInterest.toFixed(2),
    totalPaid: totalPaid.plus(balloon).toFixed(2),
    periodicRatePct: t.rate.times(100).toSignificantDigits(10).toString(),
  };
}
